/**
 * Parsers for Google Takeout exports that we turn into "My Places" rows.
 *
 * We handle these inputs:
 *   1. Saved Places CSV (Takeout "Saved" — lists, Want to go, etc.) — Title, Note, URL, Comment
 *   2. Maps GeoJSON (Takeout "Maps" / "Maps (your places)" — e.g. Saved Places.json, FeatureCollection)
 *   3. My Maps KML/KMZ       — <Placemark><Point><coordinates>lng,lat,alt</coordinates></Point></Placemark>
 *   4. Semantic Location History JSON (legacy, per-month) — timelineObjects[].placeVisit.location
 *   5. location-history.json (post-2024) — semanticSegments / visits with geo: or E7 coords
 *
 * Everything is pure and client-side. A separate parseTakeoutZip entry point
 * uses jszip (lazy-loaded so it doesn't bloat the bundle) to dispatch to the
 * right parser for each inner file.
 *
 * We also handle dedup (both against existing My Places and within the same
 * upload) and aggregation of timeline visits so the user sees one row per
 * distinct place with the right visit count & total duration.
 */

import { placeService, type Place } from './placeService'

// ───────── types ─────────

export type ImportSource =
  | 'saved_csv'
  | 'maps_geojson'
  | 'commute_routes_json'
  | 'my_maps_kml'
  | 'semantic_history'
  | 'timeline_json'
  | 'maps_link_paste'

export interface ImportCandidate {
  source: ImportSource
  title: string
  address?: string | null
  /** null means we couldn't extract coordinates — user must geocode before import */
  latitude: number | null
  longitude: number | null
  google_place_id: string | null
  /** timeline only */
  visitCount?: number
  totalMinutes?: number
  firstVisitedIso?: string
  lastVisitedIso?: string
  tags: string[]
  /** populated by dedupeAgainst() */
  dedupWith?: {
    place_id: number
    reason: 'google_place_id' | 'proximity'
    existingTitle: string
  }
  /** client-only stable key for React lists + selection state */
  _key: string
}

/** Minimal subset we POST to /api/places/import */
export interface ImportPayload {
  title: string
  address?: string | null
  latitude: number
  longitude: number
  google_place_id?: string | null
  tags?: string[]
  radius_m?: number
}

// ───────── helpers ─────────

function e7(value: number | undefined): number | null {
  if (value == null || !Number.isFinite(value)) return null
  return value / 1e7
}

function safeString(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value
  if (value == null) return fallback
  return String(value)
}

function minutesBetween(startIso: string, endIso: string): number {
  const start = Date.parse(startIso)
  const end = Date.parse(endIso)
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0
  return (end - start) / 60000
}

let keyCounter = 0
function nextKey(prefix: string): string {
  keyCounter += 1
  return `${prefix}-${keyCounter}-${Date.now()}`
}

/**
 * Haversine distance in meters. Cheap enough for O(n*m) dedup over typical
 * place collections (≤ a few hundred existing, ≤ few thousand candidates).
 */
export function haversineMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371000
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const sa =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(sa), Math.sqrt(1 - sa))
  return R * c
}

// ───────── CSV (Saved Places) ─────────

/**
 * Minimal RFC 4180 reader — handles quoted fields with commas and doubled
 * quotes inside. Saved Places CSVs don't use embedded newlines in fields,
 * so we split lines first and then parse each row.
 */
function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i]
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          cur += '"'
          i += 1
        } else {
          inQuotes = false
        }
      } else {
        cur += ch
      }
    } else if (ch === ',') {
      out.push(cur)
      cur = ''
    } else if (ch === '"') {
      inQuotes = true
    } else {
      cur += ch
    }
  }
  out.push(cur)
  return out
}

export function parseSavedPlacesCsv(text: string): ImportCandidate[] {
  if (!text || !text.trim()) return []
  // Excel / Takeout sometimes prefix the file with a UTF-8 BOM — breaks header matching.
  const normalized = text.replace(/^\uFEFF/, '')
  const lines = normalized.split(/\r?\n/).filter((l) => l.length > 0)
  if (lines.length === 0) return []

  const header = parseCsvLine(lines[0]).map((h) => h.trim().toLowerCase())
  // Google has shipped this CSV with slightly different column names over the
  // years. Accept any of these aliases for each logical column.
  const titleIdx = header.findIndex(
    (h) =>
      h === 'title' ||
      h === 'name' ||
      h === 'place name' ||
      h === 'location name',
  )
  const urlIdx = header.findIndex(
    (h) =>
      h === 'url' ||
      h === 'link' ||
      h === 'google maps url' ||
      h === 'maps url' ||
      (h.includes('url') && (h.includes('map') || h.includes('google'))),
  )
  const noteIdx = header.findIndex(
    (h) => h === 'note' || h === 'comment' || h === 'description',
  )
  const addressIdx = header.findIndex((h) => h === 'address')

  const out: ImportCandidate[] = []
  for (let i = 1; i < lines.length; i += 1) {
    const cells = parseCsvLine(lines[i])
    const title = (titleIdx >= 0 ? cells[titleIdx] : cells[0] || '').trim()
    if (!title) continue
    const url = urlIdx >= 0 ? (cells[urlIdx] || '').trim() : ''
    const address = addressIdx >= 0 ? (cells[addressIdx] || '').trim() : ''
    const note = noteIdx >= 0 ? (cells[noteIdx] || '').trim() : ''

    const parsed = url ? placeService.parseMapsLink(url) : null
    out.push({
      source: 'saved_csv',
      title,
      address: address || null,
      latitude: parsed?.lat ?? null,
      longitude: parsed?.lng ?? null,
      google_place_id: parsed?.place_id ?? null,
      tags: ['imported:google-saved', ...(note ? [`note:${note.slice(0, 32)}`] : [])],
      _key: nextKey('csv'),
    })
  }
  return out
}

// ───────── KML (My Maps) ─────────

export function parseMyMapsKml(text: string): ImportCandidate[] {
  if (!text || !text.trim()) return []
  let doc: Document
  try {
    doc = new DOMParser().parseFromString(text, 'text/xml')
  } catch {
    return []
  }
  const parserError = doc.getElementsByTagName('parsererror')[0]
  if (parserError) return []

  const out: ImportCandidate[] = []
  const placemarks = doc.getElementsByTagName('Placemark')
  for (let i = 0; i < placemarks.length; i += 1) {
    const pm = placemarks[i]
    const nameEl = pm.getElementsByTagName('name')[0]
    const descEl = pm.getElementsByTagName('description')[0]
    const addressEl = pm.getElementsByTagName('address')[0]
    const coordsEl = pm.getElementsByTagName('coordinates')[0]
    if (!coordsEl) continue

    // KML coordinates are space/newline separated tuples "lng,lat[,alt]".
    // For a <Point> there's usually exactly one tuple.
    const raw = (coordsEl.textContent || '').trim().split(/\s+/)[0]
    if (!raw) continue
    const parts = raw.split(',')
    const lng = parseFloat(parts[0])
    const lat = parseFloat(parts[1])
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue

    const title = (nameEl?.textContent || '').trim() || 'Untitled'
    const address = (addressEl?.textContent || '').trim() || null
    const description = (descEl?.textContent || '').trim()

    out.push({
      source: 'my_maps_kml',
      title,
      address,
      latitude: lat,
      longitude: lng,
      google_place_id: null,
      tags: [
        'imported:google-saved',
        ...(description ? [`note:${description.slice(0, 32)}`] : []),
      ],
      _key: nextKey('kml'),
    })
  }
  return out
}

// ───────── Semantic Location History (legacy per-month) ─────────

interface RawPlaceVisit {
  placeVisit?: {
    location?: {
      latitudeE7?: number
      longitudeE7?: number
      placeId?: string
      name?: string
      address?: string
    }
    duration?: {
      startTimestamp?: string
      endTimestamp?: string
    }
  }
}

export function parseSemanticHistory(jsonText: string): ImportCandidate[] {
  if (!jsonText) return []
  let data: { timelineObjects?: RawPlaceVisit[] }
  try {
    data = JSON.parse(jsonText)
  } catch {
    return []
  }
  const objects = Array.isArray(data?.timelineObjects) ? data.timelineObjects : []
  const out: ImportCandidate[] = []
  for (const obj of objects) {
    const visit = obj?.placeVisit
    if (!visit?.location) continue
    const lat = e7(visit.location.latitudeE7)
    const lng = e7(visit.location.longitudeE7)
    if (lat == null || lng == null) continue
    const startIso = visit.duration?.startTimestamp
    const endIso = visit.duration?.endTimestamp
    const mins = startIso && endIso ? minutesBetween(startIso, endIso) : 0
    out.push({
      source: 'semantic_history',
      title: safeString(visit.location.name, 'Visited place'),
      address: safeString(visit.location.address) || null,
      latitude: lat,
      longitude: lng,
      google_place_id: visit.location.placeId || null,
      visitCount: 1,
      totalMinutes: Math.round(mins),
      firstVisitedIso: startIso,
      lastVisitedIso: endIso,
      tags: ['imported:google-timeline'],
      _key: nextKey('sem'),
    })
  }
  return out
}

// ───────── location-history.json (post-2024 single file) ─────────

interface RawTimelineVisit {
  visit?: {
    topCandidate?: {
      placeId?: string
      semanticType?: string
      placeLocation?: string // e.g. "geo:37.7749,-122.4194"
      latitudeE7?: number
      longitudeE7?: number
      latitude?: number
      longitude?: number
    }
    hierarchyLevelZeroPlaceId?: string
    probability?: number
  }
  startTime?: string
  endTime?: string
  // sometimes "place" field carries name/address
  place?: {
    name?: string
    address?: string
  }
}

function parseGeoUri(uri: string | undefined): { lat: number; lng: number } | null {
  if (!uri) return null
  const m = uri.match(/geo:(-?\d+\.?\d*),(-?\d+\.?\d*)/)
  if (!m) return null
  const lat = parseFloat(m[1])
  const lng = parseFloat(m[2])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

function coordsFromVisit(visit: RawTimelineVisit['visit']): { lat: number; lng: number } | null {
  const tc = visit?.topCandidate
  if (!tc) return null
  const geo = parseGeoUri(tc.placeLocation)
  if (geo) return geo
  if (tc.latitudeE7 != null && tc.longitudeE7 != null) {
    const lat = e7(tc.latitudeE7)
    const lng = e7(tc.longitudeE7)
    if (lat != null && lng != null) return { lat, lng }
  }
  if (
    typeof tc.latitude === 'number' &&
    typeof tc.longitude === 'number' &&
    Number.isFinite(tc.latitude) &&
    Number.isFinite(tc.longitude)
  ) {
    return { lat: tc.latitude, lng: tc.longitude }
  }
  return null
}

export function parseTimelineJson(jsonText: string): ImportCandidate[] {
  if (!jsonText) return []
  let data: unknown
  try {
    data = JSON.parse(jsonText)
  } catch {
    return []
  }
  // Google has used a couple of top-level shapes:
  //   { "semanticSegments": [...] }
  //   [ ... ]    (direct array)
  const rows: RawTimelineVisit[] = Array.isArray(data)
    ? (data as RawTimelineVisit[])
    : Array.isArray((data as any)?.semanticSegments)
      ? ((data as any).semanticSegments as RawTimelineVisit[])
      : []
  const out: ImportCandidate[] = []
  for (const row of rows) {
    const v = row?.visit
    if (!v) continue
    const coords = coordsFromVisit(v)
    if (!coords) continue
    const mins =
      row.startTime && row.endTime ? minutesBetween(row.startTime, row.endTime) : 0
    out.push({
      source: 'timeline_json',
      title: safeString(row.place?.name, 'Visited place'),
      address: safeString(row.place?.address) || null,
      latitude: coords.lat,
      longitude: coords.lng,
      google_place_id:
        v.topCandidate?.placeId || v.hierarchyLevelZeroPlaceId || null,
      visitCount: 1,
      totalMinutes: Math.round(mins),
      firstVisitedIso: row.startTime,
      lastVisitedIso: row.endTime,
      tags: ['imported:google-timeline'],
      _key: nextKey('tl'),
    })
  }
  return out
}

// ───────── Maps Takeout GeoJSON (Saved Places.json, starred / labeled pins) ─────────

function parseCoordNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim()) {
    const n = parseFloat(v.trim())
    return Number.isFinite(n) ? n : null
  }
  return null
}

/**
 * Many Takeout GeoJSON files (Labeled places.json, Saved Places.json) nest
 * lat/lng under properties.Location with string values, and may omit geometry.
 */
function coordsFromGoogleMapsFeatureProps(
  props: Record<string, unknown>,
): { lat: number; lng: number } | null {
  const loc = props.Location ?? props.location
  if (loc && typeof loc === 'object' && !Array.isArray(loc)) {
    const o = loc as Record<string, unknown>
    const lat = parseCoordNumber(o.Latitude ?? o.latitude ?? o.lat)
    const lng = parseCoordNumber(o.Longitude ?? o.longitude ?? o.lng)
    if (lat != null && lng != null) return { lat, lng }
  }
  const latTop = parseCoordNumber(props.Latitude ?? props.latitude)
  const lngTop = parseCoordNumber(props.Longitude ?? props.longitude)
  if (latTop != null && lngTop != null) return { lat: latTop, lng: lngTop }
  return null
}

function mapsUrlFromProps(props: Record<string, unknown>): string {
  for (const k of Object.keys(props)) {
    const lower = k.toLowerCase()
    if ((lower.includes('maps') || lower.includes('google')) && lower.includes('url')) {
      const v = props[k]
      if (typeof v === 'string' && v.trim()) return v.trim()
    }
  }
  return ''
}

function pointCoordsFromGeometry(geometry: {
  type?: string
  coordinates?: unknown
} | null | undefined): { lat: number; lng: number } | null {
  if (!geometry || geometry.type !== 'Point') return null
  const c = geometry.coordinates
  if (Array.isArray(c) && c.length >= 2) {
    const lng = Number(c[0])
    const lat = Number(c[1])
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng }
    return null
  }
  if (c && typeof c === 'object' && !Array.isArray(c)) {
    const o = c as Record<string, unknown>
    const latRaw = o.latitude ?? o.lat
    const lngRaw = o.longitude ?? o.lng
    if (typeof latRaw === 'number' && typeof lngRaw === 'number') {
      return { lat: latRaw, lng: lngRaw }
    }
  }
  return null
}

/**
 * Google Takeout "Maps" / "Maps (your places)" often exports starred & labeled
 * pins as GeoJSON FeatureCollection. Coordinates may be standard GeoJSON
 * [lng,lat] or a Google "latlng" object { latitude, longitude }.
 */
export function parseMapsGeoJson(jsonText: string): ImportCandidate[] {
  if (!jsonText?.trim()) return []
  let data: { type?: string; features?: unknown[] }
  try {
    data = JSON.parse(jsonText.replace(/^\uFEFF/, ''))
  } catch {
    return []
  }
  if (data?.type !== 'FeatureCollection' || !Array.isArray(data.features)) return []

  const out: ImportCandidate[] = []
  for (const raw of data.features) {
    const f = raw as {
      type?: string
      geometry?: { type?: string; coordinates?: unknown } | null
      properties?: Record<string, unknown>
      property?: Record<string, unknown>
    }
    if (!f) continue
    const props = (f.properties ?? f.property ?? {}) as Record<string, unknown>

    let coords = f.geometry ? pointCoordsFromGeometry(f.geometry) : null
    if (!coords) coords = coordsFromGoogleMapsFeatureProps(props)
    if (!coords) continue

    const rawTitle = [
      props.name,
      props.Name,
      props.title,
      props.Title,
      props['place name'],
      props['Feature Name'],
    ].find((x): x is string => typeof x === 'string' && x.trim().length > 0)

    const loc = props.Location ?? props.location
    const locAddr =
      loc && typeof loc === 'object' && !Array.isArray(loc)
        ? (loc as Record<string, unknown>).Address ??
          (loc as Record<string, unknown>).address
        : undefined
    const addressFromLoc = typeof locAddr === 'string' && locAddr.trim() ? locAddr.trim() : null

    const addressRaw = props.address ?? props.Address ?? props.location
    const addressFlat =
      typeof addressRaw === 'string' && addressRaw.trim() ? addressRaw.trim() : null
    const address = addressFromLoc ?? addressFlat

    const title =
      rawTitle?.trim() ||
      (address ? address.slice(0, 120) : null) ||
      'Imported place'

    let url = ''
    for (const v of [props.url, props.URL, props.placeUrl, props.place_url]) {
      if (typeof v === 'string' && v.trim()) {
        url = v.trim()
        break
      }
    }
    if (!url) url = mapsUrlFromProps(props)

    let google_place_id: string | null = null
    if (url) {
      const parsed = placeService.parseMapsLink(url)
      google_place_id = parsed?.place_id ?? null
    }

    out.push({
      source: 'maps_geojson',
      title,
      address,
      latitude: coords.lat,
      longitude: coords.lng,
      google_place_id,
      tags: ['imported:google-maps-geojson'],
      _key: nextKey('gj'),
    })
  }
  return out
}

function peekIsMapsGeoJson(peek: unknown): boolean {
  if (!peek || typeof peek !== 'object') return false
  const o = peek as Record<string, unknown>
  return (
    o.type === 'FeatureCollection' &&
    Array.isArray(o.features) &&
    o.features.length > 0
  )
}

// ───────── Maps Commute routes (Takeout Maps → Commute routes.json) ─────────

function peekIsCommuteRoutesJson(peek: unknown): boolean {
  if (!peek || typeof peek !== 'object') return false
  const trips = (peek as Record<string, unknown>).trips
  if (!Array.isArray(trips) || trips.length === 0) return false
  return trips.some((t) => {
    if (!t || typeof t !== 'object') return false
    const pv =
      (t as Record<string, unknown>).place_visit ??
      (t as Record<string, unknown>).placeVisit
    return Array.isArray(pv) && pv.length > 0
  })
}

function titleFromCommuteSemanticType(semanticType: string | undefined): string {
  const s = semanticType?.trim() || ''
  if (!s || s === 'TYPE_UNKNOWN') return 'Commute destination'
  if (s === 'TYPE_HOME') return 'Home (commute)'
  if (s === 'TYPE_WORK') return 'Work (commute)'
  return s
    .replace(/^TYPE_/, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

/**
 * Takeout path: Maps/Commute routes/Commute routes.json
 * Shape: { trips: [ { place_visit: [ { place?: { lat_lng, semantic_type } } ] } ] }
 */
export function parseCommuteRoutesJson(jsonText: string): ImportCandidate[] {
  if (!jsonText?.trim()) return []
  let data: { trips?: unknown[] }
  try {
    data = JSON.parse(jsonText.replace(/^\uFEFF/, ''))
  } catch {
    return []
  }
  const trips = Array.isArray(data?.trips) ? data.trips : []
  const out: ImportCandidate[] = []

  for (const trip of trips) {
    if (!trip || typeof trip !== 'object') continue
    const t = trip as Record<string, unknown>
    const visits = (Array.isArray(t.place_visit)
      ? t.place_visit
      : Array.isArray(t.placeVisit)
        ? t.placeVisit
        : []) as unknown[]

    for (const v of visits) {
      if (!v || typeof v !== 'object') continue
      const pv = v as {
        place?: {
          lat_lng?: { latitude?: number; longitude?: number }
          semantic_type?: string
          semanticType?: string
        }
      }
      const ll = pv.place?.lat_lng
      if (
        !ll ||
        typeof ll.latitude !== 'number' ||
        typeof ll.longitude !== 'number' ||
        !Number.isFinite(ll.latitude) ||
        !Number.isFinite(ll.longitude)
      ) {
        continue
      }
      const sem = pv.place?.semantic_type ?? pv.place?.semanticType
      out.push({
        source: 'commute_routes_json',
        title: titleFromCommuteSemanticType(sem),
        address: null,
        latitude: ll.latitude,
        longitude: ll.longitude,
        google_place_id: null,
        visitCount: 1,
        tags: ['imported:google-commute-routes'],
        _key: nextKey('cm'),
      })
    }
  }
  return out
}

// ───────── aggregation (timeline self-dedup) ─────────

/**
 * Merge candidates that refer to the same physical place. We key by
 * google_place_id when present, otherwise by coordinates rounded to 5
 * decimals (~1.1 m precision — tight enough that two visits to the same
 * building collapse but adjacent storefronts stay distinct).
 */
export function aggregateTimelineVisits(
  candidates: ImportCandidate[],
): ImportCandidate[] {
  const byKey = new Map<string, ImportCandidate>()
  const passthrough: ImportCandidate[] = []
  for (const c of candidates) {
    if (
      c.source !== 'semantic_history' &&
      c.source !== 'timeline_json' &&
      c.source !== 'commute_routes_json'
    ) {
      passthrough.push(c)
      continue
    }
    const key = c.google_place_id
      ? `pid:${c.google_place_id}`
      : `geo:${(c.latitude ?? 0).toFixed(5)}:${(c.longitude ?? 0).toFixed(5)}`
    const existing = byKey.get(key)
    if (!existing) {
      byKey.set(key, { ...c, visitCount: c.visitCount ?? 1 })
    } else {
      existing.visitCount = (existing.visitCount ?? 0) + (c.visitCount ?? 1)
      existing.totalMinutes =
        (existing.totalMinutes ?? 0) + (c.totalMinutes ?? 0)
      // Keep the earliest first-visit and latest last-visit.
      if (
        c.firstVisitedIso &&
        (!existing.firstVisitedIso || c.firstVisitedIso < existing.firstVisitedIso)
      ) {
        existing.firstVisitedIso = c.firstVisitedIso
      }
      if (
        c.lastVisitedIso &&
        (!existing.lastVisitedIso || c.lastVisitedIso > existing.lastVisitedIso)
      ) {
        existing.lastVisitedIso = c.lastVisitedIso
      }
      // Prefer a non-placeholder title/address.
      if (
        (existing.title === 'Visited place' || !existing.title) &&
        c.title &&
        c.title !== 'Visited place'
      ) {
        existing.title = c.title
      }
      if (
        (existing.title === 'Commute destination' || !existing.title) &&
        c.title &&
        c.title !== 'Commute destination'
      ) {
        existing.title = c.title
      }
      if (!existing.address && c.address) existing.address = c.address
      if (!existing.google_place_id && c.google_place_id) {
        existing.google_place_id = c.google_place_id
      }
    }
  }
  return [...passthrough, ...byKey.values()]
}

// ───────── dedup against existing My Places ─────────

export function dedupeAgainst(
  existing: Place[],
  candidates: ImportCandidate[],
): ImportCandidate[] {
  if (candidates.length === 0) return candidates
  const byPlaceId = new Map<string, Place>()
  for (const p of existing) {
    if (p.google_place_id) byPlaceId.set(p.google_place_id, p)
  }
  return candidates.map((c) => {
    // 1. google_place_id match wins outright.
    if (c.google_place_id && byPlaceId.has(c.google_place_id)) {
      const match = byPlaceId.get(c.google_place_id)!
      return {
        ...c,
        dedupWith: {
          place_id: match.id,
          reason: 'google_place_id',
          existingTitle: match.title,
        },
      }
    }
    // 2. proximity < 80 m AND title contains (either direction).
    if (c.latitude == null || c.longitude == null) return c
    const lowerTitle = c.title.toLowerCase()
    for (const p of existing) {
      const dist = haversineMeters(
        { lat: c.latitude, lng: c.longitude },
        { lat: p.latitude, lng: p.longitude },
      )
      if (dist > 80) continue
      const existingTitle = (p.title || '').toLowerCase()
      if (
        existingTitle &&
        (lowerTitle.includes(existingTitle) || existingTitle.includes(lowerTitle))
      ) {
        return {
          ...c,
          dedupWith: {
            place_id: p.id,
            reason: 'proximity',
            existingTitle: p.title,
          },
        }
      }
    }
    return c
  })
}

// ───────── ZIP (lazy jszip) ─────────

/**
 * Google Maps Takeout CSVs — path must look like a places list, not Reviews
 * or other Maps CSVs (those use different columns and would yield 0 rows).
 */
function looksLikeGoogleMapsPlacesCsvPath(pathLower: string): boolean {
  if (!pathLower.endsWith('.csv')) return false
  // Takeout nests list CSVs under a "Saved" folder — file names may be arbitrary (e.g. "Tokyo.csv").
  if (pathLower.includes('/saved/') || pathLower.includes('\\saved\\')) return true
  if (pathLower.includes('saved')) return true
  if (pathLower.includes('place')) return true
  if (pathLower.includes('starred')) return true
  if (pathLower.includes('want') && pathLower.includes('go')) return true
  if (pathLower.includes('labeled') || pathLower.includes('labelled')) return true
  if (pathLower.includes('favorite') || pathLower.includes('favourite')) return true
  if (pathLower.includes('bookmark')) return true
  if (pathLower.includes('followed')) return true
  if (pathLower.includes('custom') && pathLower.includes('list')) return true
  return false
}

/** Matchers for the four file types inside a Takeout ZIP. */
function classifyFilename(name: string): ImportSource | null {
  const lower = name.toLowerCase()
  if (lower.endsWith('.csv') && looksLikeGoogleMapsPlacesCsvPath(lower)) {
    return 'saved_csv'
  }
  if (lower.endsWith('.kml') || lower.endsWith('.kmz')) return 'my_maps_kml'
  if (
    lower.endsWith('.json') &&
    (lower.includes('semantic location history') ||
      lower.match(/\d{4}_[a-z]+\.json$/))
  ) {
    return 'semantic_history'
  }
  if (
    lower.endsWith('.json') &&
    // "location-history.json" (phone export) and "Timeline.json" (Takeout)
    // both ship the post-2024 semanticSegments / top-level-array shape.
    // "Records.json" is intentionally skipped — it's the raw high-frequency
    // sampler output and doesn't describe discrete place visits.
    (lower.includes('location-history') || lower.endsWith('timeline.json'))
  ) {
    return 'timeline_json'
  }
  if (
    lower.endsWith('.json') &&
    lower.includes('commute') &&
    (lower.includes('route') || lower.includes('routes'))
  ) {
    return 'commute_routes_json'
  }
  if (lower.endsWith('.geojson')) return 'maps_geojson'
  if (
    lower.endsWith('.json') &&
    (lower.includes('saved places') ||
      (lower.includes('saved') && lower.includes('place')) ||
      lower.includes('labeled') ||
      lower.includes('labelled') ||
      lower.includes('aliased'))
  ) {
    return 'maps_geojson'
  }
  return null
}

export interface ZipParseResult {
  candidates: ImportCandidate[]
  perFile: Array<{ filename: string; source: ImportSource; count: number }>
  unrecognized: string[]
}

/** Extra detail when parsing a Takeout .zip (for user-facing error hints). */
export interface ZipImportDiagnostics {
  unrecognized: string[]
  recognizedFiles: Array<{ filename: string; source: ImportSource; count: number }>
}

export interface ParseSingleFileResult {
  candidates: ImportCandidate[]
  source: ImportSource | 'zip' | null
  zipDiagnostics?: ZipImportDiagnostics
}

export async function parseTakeoutZip(file: File): Promise<ZipParseResult> {
  // Lazy import — keeps jszip out of the main bundle until someone clicks
  // Import from Google.
  const { default: JSZip } = await import('jszip')
  const zip = await JSZip.loadAsync(file)

  const candidates: ImportCandidate[] = []
  const perFile: ZipParseResult['perFile'] = []
  const unrecognized: string[] = []

  const entries = Object.values(zip.files).filter((e) => !e.dir)
  for (const entry of entries) {
    const lower = entry.name.toLowerCase()
    let text: string
    if (lower.endsWith('.kmz')) {
      const buf = await entry.async('arraybuffer')
      const inner = await JSZip.loadAsync(buf)
      const kmlEntry = Object.values(inner.files).find(
        (e) => !e.dir && e.name.toLowerCase().endsWith('.kml'),
      )
      text = kmlEntry ? await kmlEntry.async('string') : ''
    } else {
      text = await entry.async('string')
    }

    let kind = classifyFilename(entry.name)
    if (!kind && lower.endsWith('.json')) {
      try {
        const peek = JSON.parse(text)
        if (Array.isArray(peek?.timelineObjects)) kind = 'semantic_history'
        else if (Array.isArray(peek) || Array.isArray(peek?.semanticSegments)) {
          kind = 'timeline_json'
        }         else if (peekIsCommuteRoutesJson(peek)) {
          kind = 'commute_routes_json'
        } else if (peekIsMapsGeoJson(peek)) {
          kind = 'maps_geojson'
        }
      } catch {
        /* ignore */
      }
    }

    if (!kind) {
      unrecognized.push(entry.name)
      continue
    }
    let batch: ImportCandidate[] = []
    if (kind === 'saved_csv') batch = parseSavedPlacesCsv(text)
    else if (kind === 'maps_geojson') batch = parseMapsGeoJson(text)
    else if (kind === 'my_maps_kml') batch = parseMyMapsKml(text)
    else if (kind === 'semantic_history') batch = parseSemanticHistory(text)
    else if (kind === 'timeline_json') batch = parseTimelineJson(text)
    else if (kind === 'commute_routes_json') batch = parseCommuteRoutesJson(text)

    candidates.push(...batch)
    perFile.push({ filename: entry.name, source: kind, count: batch.length })
  }
  return { candidates, perFile, unrecognized }
}

// ───────── convenience: route by filename ─────────

export async function parseSingleFile(file: File): Promise<ParseSingleFileResult> {
  const name = file.name.toLowerCase()
  if (name.endsWith('.zip')) {
    const res = await parseTakeoutZip(file)
    return {
      candidates: res.candidates,
      source: 'zip',
      zipDiagnostics: {
        unrecognized: res.unrecognized,
        recognizedFiles: res.perFile.map((p) => ({
          filename: p.filename,
          source: p.source,
          count: p.count,
        })),
      },
    }
  }
  if (name.endsWith('.kmz')) {
    const { default: JSZip } = await import('jszip')
    const z = await JSZip.loadAsync(await file.arrayBuffer())
    const kmlEntry = Object.values(z.files).find(
      (e) => !e.dir && e.name.toLowerCase().endsWith('.kml'),
    )
    const kmlText = kmlEntry ? await kmlEntry.async('string') : ''
    return { candidates: parseMyMapsKml(kmlText), source: 'my_maps_kml' }
  }
  const text = await file.text()
  if (name.endsWith('.csv')) {
    return { candidates: parseSavedPlacesCsv(text), source: 'saved_csv' }
  }
  if (name.endsWith('.kml')) {
    return { candidates: parseMyMapsKml(text), source: 'my_maps_kml' }
  }
  if (name.endsWith('.geojson')) {
    return { candidates: parseMapsGeoJson(text), source: 'maps_geojson' }
  }
  if (name.endsWith('.json')) {
    // Disambiguate between the two JSON shapes by looking at top-level keys.
    try {
      const peek = JSON.parse(text.replace(/^\uFEFF/, ''))
      if (Array.isArray(peek?.timelineObjects)) {
        return { candidates: parseSemanticHistory(text), source: 'semantic_history' }
      }
      if (Array.isArray(peek) || Array.isArray(peek?.semanticSegments)) {
        return { candidates: parseTimelineJson(text), source: 'timeline_json' }
      }
      if (peekIsCommuteRoutesJson(peek)) {
        return { candidates: parseCommuteRoutesJson(text), source: 'commute_routes_json' }
      }
      if (peekIsMapsGeoJson(peek)) {
        return { candidates: parseMapsGeoJson(text), source: 'maps_geojson' }
      }
    } catch {
      // Fall through.
    }
    return { candidates: [], source: null }
  }
  return { candidates: [], source: null }
}

// ───────── convert to POST payload ─────────

export function candidateToPayload(c: ImportCandidate): ImportPayload | null {
  if (c.latitude == null || c.longitude == null) return null
  return {
    title: c.title,
    address: c.address ?? null,
    latitude: c.latitude,
    longitude: c.longitude,
    google_place_id: c.google_place_id ?? null,
    tags: c.tags,
  }
}
